import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'antd';
import { PlayCircleOutlined } from '@ant-design/icons';
import Hls from 'hls.js';
import flvjs from 'flv.js';
import { playSrsWebRtc, getWebRtcUrl } from '../../utils/srsWebRtcPlayer';
import {
  PROTOCOLS,
  PROTOCOL_RETRY_ORDER,
  formatFullscreenClock,
  getVideoContainerStyle,
  getVideoStyle
} from '../../utils/multiWindowLive';
import ResourceManager from './ResourceManager';
import {
  FullscreenInfoBar,
  VideoHeader,
  VideoStatusOverlay,
  RotateHud,
  VideoErrorPanel,
  VideoControlsBar,
  VideoFooterStatus
} from './VideoPlayerParts';

const MultiWindowVideoPlayer = ({ 
  streamKey, 
  memberName, 
  protocol = 'webrtc',
  onError, 
  onSuccess,
  onStop, // 新增：停止回调函数
  isFullscreen = false,
  onFullscreenChange,
  forceMuted = false, // 新增：强制静音状态
  /** 手机端：不渲染悬停控制条，避免触摸 :hover 粘滞导致画面闪烁 */
  hideOverlayControls = false,
  /** 队员设备电量（全屏信息条用） */
  batteryLevel = null,
  /** 位置/电量最近上报时间 */
  lastSeen = null,
  /** 按住画面拖拽换窗（不占额外一行 UI） */
  slotDragIndex = null,
  onSlotDragStart,
  onSlotDragEnd
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // 改为false，不自动加载
  const [error, setError] = useState(null);
  const [streamUrls, setStreamUrls] = useState({});
  const [currentProtocol, setCurrentProtocol] = useState(protocol);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isInitialized, setIsInitialized] = useState(false); // 新增：是否已初始化
  const [currentDelay, setCurrentDelay] = useState(0); // 新增：当前延迟时间
  const [isAccelerating, setIsAccelerating] = useState(false); // 新增：是否正在加速播放
  const [lastPlaybackTime, setLastPlaybackTime] = useState(0); // 新增：上次播放时间
  const [retryCount, setRetryCount] = useState(0); // 新增：重试次数
  const [lastRetryTime, setLastRetryTime] = useState(0); // 新增：上次重试时间
  const [isRetrying, setIsRetrying] = useState(false); // 新增：是否正在重试
  
  // 视频方向：自动按宽高比竖排显示（手机竖拍无需再手拧）
  const [videoOrientation, setVideoOrientation] = useState('landscape'); // 'landscape' | 'portrait'
  const [rotationAngle, setRotationAngle] = useState(0); // 手动/纠正角度：0, 90, 180, 270
  /** 画面翻转：左右镜像 / 上下颠倒（可与旋转叠加，全屏可用） */
  const [flipHorizontal, setFlipHorizontal] = useState(false);
  const [flipVertical, setFlipVertical] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
  /** 自动方向：按 videoWidth/Height 识别竖屏并保持正向（默认开） */
  const [autoOrient, setAutoOrient] = useState(true);
  const autoOrientRef = useRef(true);
  autoOrientRef.current = autoOrient;
  /** 用户手动拧过之后，暂停自动改角度，直到重新打开自动或点重置 */
  const manualRotateRef = useRef(false);
  /** 已稳定识别过的宽高，避免反复 setState 引发手机端屏闪 */
  const lastOrientKeyRef = useRef('');
  const orientPollRef = useRef(null);
  /** 单击画面显示旋转按钮；延时区分双击全屏 */
  const [showRotateHud, setShowRotateHud] = useState(false);
  const rotateClickTimerRef = useRef(null);
  const surfaceDraggedRef = useRef(false);
  /** 全屏信息条：当前时钟 */
  const [fullscreenClock, setFullscreenClock] = useState('');
  
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const flvRef = useRef(null);
  const webrtcCloseRef = useRef(null);
  const delayCheckIntervalRef = useRef(null); // 新增：延迟检测定时器引用
  const accelerationIntervalRef = useRef(null); // 新增：加速播放定时器引用
  const containerRef = useRef(null);
  const retryTimeoutRef = useRef(null); // 新增：重试定时器引用
  const orientTimerRef = useRef(null);

  // 计算最终的静音状态：用户手动静音或强制静音
  const finalMutedState = isMuted || forceMuted;

  const cleanupMediaPlayers = () => {
    lastOrientKeyRef.current = '';
    if (orientPollRef.current) {
      clearInterval(orientPollRef.current);
      orientPollRef.current = null;
    }
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch (e) { /* ignore */ }
      hlsRef.current = null;
    }
    if (flvRef.current) {
      try {
        flvRef.current.pause();
        flvRef.current.unload();
        flvRef.current.detachMediaElement();
        flvRef.current.destroy();
      } catch (e) { /* ignore */ }
      flvRef.current = null;
    }
    if (webrtcCloseRef.current) {
      try { webrtcCloseRef.current(); } catch (e) { /* ignore */ }
      webrtcCloseRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      } catch (e) { /* ignore */ }
    }
  };

  // 获取流媒体URL
  const fetchStreamUrls = async () => {
    try {
      // 使用与指挥系统页面相同的直接fetch调用方式
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/multi-protocol-streaming/urls/${streamKey}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setStreamUrls(data.urls);
      } else {
        console.error('获取流媒体URL失败:', data.message);
        setError('获取流媒体URL失败');
      }
    } catch (error) {
      console.error('获取流媒体URL失败:', error);
      setError('获取流媒体URL失败');
    }
  };

  /**
   * 根据解码后的 videoWidth/Height 识别横/竖屏（PC/手机共用）。
   * 优先用 video 元素真实帧尺寸；track.settings 常与编码方向不一致，仅作兜底。
   * 竖屏：height>width → object-fit contain 正向显示；侧躺横编码流仍可手动旋转。
   */
  const detectVideoOrientation = useCallback((force = false) => {
    const video = videoRef.current;
    if (!video) return false;

    let width = video.videoWidth || 0;
    let height = video.videoHeight || 0;

    // 解码尺寸尚未就绪时，再用 track 兜底
    if (!width || !height) {
      try {
        const track = video.srcObject?.getVideoTracks?.()?.[0];
        const settings = track?.getSettings?.();
        if (settings?.width && settings?.height) {
          width = settings.width;
          height = settings.height;
        }
      } catch (_) { /* ignore */ }
    }

    if (!width || !height) return false;

    const orientKey = `${width}x${height}`;
    // 尺寸未变则跳过（含 force），避免定时轮询反复 setState 引起闪烁
    if (lastOrientKeyRef.current === orientKey) return true;
    lastOrientKeyRef.current = orientKey;

    const aspectRatio = width / height;
    const newOrientation = aspectRatio < 1 ? 'portrait' : 'landscape';

    setVideoDimensions({ width, height });
    setVideoOrientation((prev) => (prev === newOrientation ? prev : newOrientation));

    if (autoOrientRef.current && !manualRotateRef.current) {
      // 正向竖/横屏都不强制拧角度；仅纠正此前手动残留的角度
      setRotationAngle((prev) => (prev === 0 ? prev : 0));
    }
    return true;
  }, []);

  const stopOrientPoll = useCallback(() => {
    if (orientPollRef.current) {
      clearInterval(orientPollRef.current);
      orientPollRef.current = null;
    }
  }, []);

  const scheduleOrientDetect = useCallback(() => {
    if (orientTimerRef.current) clearTimeout(orientTimerRef.current);
    // PC WebRTC 尺寸常晚到：短轮询直到识别成功，或最多约 8 秒
    stopOrientPoll();
    let tries = 0;
    orientPollRef.current = setInterval(() => {
      tries += 1;
      const ok = detectVideoOrientation(false);
      if (ok || tries >= 16) stopOrientPoll();
    }, 500);

    orientTimerRef.current = setTimeout(() => {
      detectVideoOrientation(true);
    }, 200);
  }, [detectVideoOrientation, stopOrientPoll]);

  // 向左旋转90度（普通窗 / 全屏均可用）
  const rotateLeft = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    manualRotateRef.current = true;
    setAutoOrient(false);
    setShowRotateHud(true);
    setRotationAngle((prev) => {
      const newAngle = (prev - 90 + 360) % 360;
      console.log(`[${memberName}] 向左旋转，当前角度: ${newAngle}度`);
      return newAngle;
    });
  };

  // 向右旋转90度（普通窗 / 全屏均可用）
  const rotateRight = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    manualRotateRef.current = true;
    setAutoOrient(false);
    setShowRotateHud(true);
    setRotationAngle((prev) => {
      const newAngle = (prev + 90) % 360;
      console.log(`[${memberName}] 向右旋转，当前角度: ${newAngle}度`);
      return newAngle;
    });
  };

  /** 单击画面：显示/隐藏旋转按钮；双击留给全屏，不触发此逻辑 */
  const handleSurfaceClick = (e) => {
    if (e.target.closest?.('.rotate-hud')) return;
    if (!isInitialized || isLoading || error) return;
    if (surfaceDraggedRef.current) {
      surfaceDraggedRef.current = false;
      return;
    }
    if (rotateClickTimerRef.current) {
      clearTimeout(rotateClickTimerRef.current);
      rotateClickTimerRef.current = null;
      return;
    }
    rotateClickTimerRef.current = setTimeout(() => {
      rotateClickTimerRef.current = null;
      setShowRotateHud((v) => !v);
    }, 280);
  };

  const handleSurfaceDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (rotateClickTimerRef.current) {
      clearTimeout(rotateClickTimerRef.current);
      rotateClickTimerRef.current = null;
    }
    toggleFullscreen();
  };

  // 左右翻转（镜像）
  const toggleFlipHorizontal = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    manualRotateRef.current = true;
    setAutoOrient(false);
    setShowRotateHud(true);
    setFlipHorizontal((prev) => !prev);
  };

  // 上下翻转
  const toggleFlipVertical = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    manualRotateRef.current = true;
    setAutoOrient(false);
    setShowRotateHud(true);
    setFlipVertical((prev) => !prev);
  };

  // 重置旋转/翻转并恢复自动方向
  const resetRotation = () => {
    manualRotateRef.current = false;
    setRotationAngle(0);
    setFlipHorizontal(false);
    setFlipVertical(false);
    setAutoOrient(true);
    lastOrientKeyRef.current = '';
    scheduleOrientDetect();
    console.log(`[${memberName}] 重置旋转/翻转并恢复自动方向`);
  };

  const toggleAutoOrient = () => {
    const next = !autoOrient;
    setAutoOrient(next);
    if (next) {
      manualRotateRef.current = false;
      lastOrientKeyRef.current = '';
      scheduleOrientDetect();
    }
  };

  // 获取视频容器样式
  const getVideoContainerStyleLocal = () => getVideoContainerStyle();

  // 获取视频样式：始终 contain；旋转 + 翻转在普通窗与全屏均生效
  const getVideoStyleLocal = () => getVideoStyle({
    rotationAngle,
    flipHorizontal,
    flipVertical,
    isFullscreen
  });

  // 初始化HLS播放器
  // 初始化RTMP播放器

  const initHLS = () => {
    if (!Hls.isSupported()) {
      setError('浏览器不支持HLS播放');
      return false;
    }

    try {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 5,
        maxBufferLength: 4,
        maxMaxBufferLength: 8,
        maxBufferSize: 4 * 1000 * 1000,
        maxBufferHole: 0.05,
        // 只使用一种延迟设置方式，避免配置冲突
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 5,
        liveDurationInfinity: true,
        maxLoadingDelay: 1,
        highBufferWatchdogPeriod: 1,
        nudgeOffset: 0.05,
        nudgeMaxRetry: 3,
        fragLoadingMaxRetry: 2,
        fragLoadingRetryDelay: 500,
        fragLoadingMaxRetryTimeout: 32000,
        // 添加错误恢复配置
        fragLoadingRetryDelay: 1000,
        fragLoadingMaxRetry: 3,
        // 添加媒体序列错误处理
        enableSoftwareAES: true,
        // 禁用严格模式，允许媒体序列不匹配
        strictLevel: 0,
        // 移除重复的延迟设置，避免配置冲突
        // liveSyncDuration: 1,        // 已删除
        // liveMaxLatencyDuration: 2,  // 已删除
        startLevel: -1,
        abrEwmaDefaultEstimate: 500000,
        abrBandWidthFactor: 0.95,
        abrBandWidthUpFactor: 0.7,
      });

      hlsRef.current = hls;

      hls.loadSource(streamUrls.proxy?.hls || streamUrls.pull?.hls);
      hls.attachMedia(videoRef.current);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
        setIsPlaying(true);
        setError(null);
        
        // 播放成功，重置重试状态
        setRetryCount(0);
        setLastRetryTime(0);
        setIsRetrying(false);
        
        if (onSuccess) onSuccess('hls');
        console.log(`${streamKey} HLS播放成功，重试状态已重置`);
        
        scheduleOrientDetect();
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS播放错误:', data);
        if (data.fatal) {
          setError('HLS播放失败');
          if (onError) onError('hls', data);
          
          // 自动尝试智能重试
          setTimeout(() => {
            if (!isPlaying && !isRetrying) {
              smartRetry();
            }
          }, 1000);
        }
      });

      return true;
    } catch (error) {
      console.error('初始化HLS失败:', error);
      setError('HLS初始化失败');
      return false;
    }
  };

  // 初始化FLV播放器
  const initFLV = () => {
    if (!flvjs.isSupported()) {
      setError('浏览器不支持FLV播放');
      return false;
    }

    try {
      if (flvRef.current) {
        flvRef.current.destroy();
      }

      const flvPlayer = flvjs.createPlayer({
        type: 'flv',
        url: streamUrls.proxy?.flv || streamUrls.pull?.flv,
        isLive: true,
        hasAudio: true,
        hasVideo: true
      }, {
        enableStashBuffer: false,
        stashInitialSize: 64,
        // CRA/生产构建下 Worker 会报 Class extends value undefined，必须关闭
        enableWorker: false,
        lazyLoad: false,
        autoCleanupSourceBuffer: true,
        fixAudioTimestampGap: true,
        accurateSeek: false
      });

      flvRef.current = flvPlayer;
      flvPlayer.attachMediaElement(videoRef.current);
      
      // 设置加载超时 - 只有在真正没有播放成功时才显示错误
      const loadTimeout = setTimeout(() => {
        // 只有在仍然处于加载状态且没有播放成功时才显示超时错误
        if (isLoading && !isPlaying) {
          console.warn('FLV加载超时，尝试重新加载');
          setError('FLV加载超时，请检查网络连接');
          if (onError) onError('flv', { errorType: 'timeout' });
        }
      }, 10000); // 10秒超时

      flvPlayer.load();

      // 新增：延迟检测和加速播放逻辑
      // 延迟检测定时器 - 简化版本
      const startDelayDetection = () => {
        // 清理之前的定时器
        if (delayCheckIntervalRef.current) {
          clearInterval(delayCheckIntervalRef.current);
        }
        if (accelerationIntervalRef.current) {
          clearInterval(accelerationIntervalRef.current);
        }

        // 记录开始检测的时间
        const detectionStartTime = Date.now();
        let lastCurrentTime = 0;
        let noProgressCount = 0; // 连续没有进度增长的次数
        let lastStutterTime = 0; // 上次卡顿时间

        delayCheckIntervalRef.current = setInterval(() => {
          if (videoRef.current && isPlaying) {
            const currentTime = videoRef.current.currentTime;
            const now = Date.now();
            const elapsedTime = (now - detectionStartTime) / 1000;
            
            // 计算缓冲区信息
            let bufferLength = 0;
            const buffered = videoRef.current.buffered;
            if (buffered && buffered.length > 0) {
              const bufferedEnd = buffered.end(buffered.length - 1);
              bufferLength = bufferedEnd - currentTime;
            }
            
            // 检测卡顿的多种方法
            const timeDiff = currentTime - lastCurrentTime;
            const networkDelay = elapsedTime - currentTime;
            let stutterDetected = false;
            let stutterReason = '';
            
            // 方法1: 检测currentTime没有增长
            if (timeDiff < 0.1) {
              noProgressCount++;
              stutterDetected = true;
              stutterReason = 'currentTime无增长';
            } else {
              noProgressCount = 0;
            }
            
            // 方法2: 检测缓冲区不足
            if (bufferLength < 0.5) {
              stutterDetected = true;
              stutterReason = '缓冲区不足';
            }
            
            // 方法3: 检测网络延迟过高
            if (networkDelay > 8) {
              stutterDetected = true;
              stutterReason = '网络延迟过高';
            }
            
            // 方法4: 检测8秒周期卡顿
            if (lastStutterTime > 0) {
              const timeSinceLastStutter = now - lastStutterTime;
              if (timeSinceLastStutter > 7000 && timeSinceLastStutter < 9000 && noProgressCount > 2) {
                stutterDetected = true;
                stutterReason = '8秒周期卡顿';
              }
            }
            
            if (stutterDetected) {
              lastStutterTime = now;
              console.log(`${streamKey} 检测到卡顿: ${stutterReason} (currentTime: ${currentTime.toFixed(1)}, buffer: ${bufferLength.toFixed(1)}s, networkDelay: ${networkDelay.toFixed(1)}s)`);
              
              // 立即尝试加速播放
              if (!isAccelerating) {
                console.log(`${streamKey} 开始加速播放尝试修复卡顿`);
                setIsAccelerating(true);
                startAcceleration();
              }
            } else if (isAccelerating && bufferLength > 2 && networkDelay < 3) {
              // 恢复正常播放
              console.log(`${streamKey} 卡顿已修复，恢复正常播放`);
              setIsAccelerating(false);
              stopAcceleration();
            }
            
            lastCurrentTime = currentTime;
            
            // 使用网络延迟作为显示延迟（变化小时不 setState，减少手机端重渲染闪屏）
            const displayDelay = Math.max(0, Math.min(15, networkDelay));
            setCurrentDelay((prev) => (
              Math.abs(prev - displayDelay) < 0.4 ? prev : Number(displayDelay.toFixed(1))
            ));
          }
        }, 1000);
      };

      // 开始加速播放
      const startAcceleration = () => {
        if (videoRef.current) {
          console.log(`${streamKey} 开始加速播放，当前播放速度: ${videoRef.current.playbackRate}`);
          // 设置播放速度
          videoRef.current.playbackRate = 1.5; // 1.5倍速播放
          
          // 监控加速播放效果
          accelerationIntervalRef.current = setInterval(() => {
            if (videoRef.current && isAccelerating) {
              try {
                // 简化的延迟计算
                const currentTime = videoRef.current.currentTime;
                let delay = 2.0; // 默认延迟
                
                // 使用缓冲区信息计算延迟
                const buffered = videoRef.current.buffered;
                if (buffered && buffered.length > 0) {
                  const bufferedEnd = buffered.end(buffered.length - 1);
                  delay = bufferedEnd - currentTime;
                }
                
                delay = Math.max(0, Math.min(15, delay));
                
                console.log(`${streamKey} 加速播放中，延迟: ${delay.toFixed(1)}s，播放速度: ${videoRef.current.playbackRate}`);
                
                // 如果延迟仍然很大，继续加速
                if (delay > 2) {
                  const newRate = Math.min(2.0, videoRef.current.playbackRate + 0.1);
                  videoRef.current.playbackRate = newRate;
                  console.log(`${streamKey} 继续加速，新播放速度: ${newRate}`);
                }
                // 如果延迟接近正常，逐渐恢复正常速度
                else if (delay < 1.5) {
                  const newRate = Math.max(1.0, videoRef.current.playbackRate - 0.05);
                  videoRef.current.playbackRate = newRate;
                  console.log(`${streamKey} 逐渐恢复正常速度: ${newRate}`);
                }
              } catch (error) {
                console.error('加速播放监控出错:', error);
              }
            }
          }, 2000); // 每2秒调整一次播放速度
        }
      };

      // 停止加速播放
      const stopAcceleration = () => {
        if (videoRef.current) {
          videoRef.current.playbackRate = 1.0; // 恢复正常播放速度
          console.log(`${streamKey} 停止加速播放，恢复正常速度`);
        }
        if (accelerationIntervalRef.current) {
          clearInterval(accelerationIntervalRef.current);
          accelerationIntervalRef.current = null;
        }
      };

      flvPlayer.on(flvjs.Events.LOADED_METADATA, () => {
        clearTimeout(loadTimeout);
        setIsLoading(false);
        setError(null);
        
        // 播放成功，重置重试状态
        setRetryCount(0);
        setLastRetryTime(0);
        setIsRetrying(false);
        
        if (onSuccess) onSuccess('flv');
        console.log(`${streamKey} FLV播放器加载成功，重试状态已重置`);
        
        scheduleOrientDetect();
        
        // 自动开始播放，减少手动操作的时间误差
        console.log('自动开始播放...');
        videoRef.current.play().then(() => {
          console.log('播放已自动开始');
          setIsPlaying(true);
        }).catch((error) => {
          console.log('自动播放失败，需要手动点击播放: ' + error.message);
          setIsPlaying(false);
        });
        
        // 延迟启动延迟检测，确保播放真正开始
        setTimeout(() => {
          if (isPlaying) {
            startDelayDetection();
          } else {
            // 如果自动播放失败，等待手动播放后再开始检测
            const checkPlayInterval = setInterval(() => {
              if (isPlaying) {
                clearInterval(checkPlayInterval);
                startDelayDetection();
              }
            }, 500);
          }
        }, 2000); // 2秒后开始检测
      });

      flvPlayer.on(flvjs.Events.LOADING_COMPLETE, () => {
        clearTimeout(loadTimeout);
        setIsLoading(false);
        setIsPlaying(true);
        setError(null);
        console.log('FLV加载完成');
      });

      // 添加播放开始事件监听
      flvPlayer.on(flvjs.Events.MEDIA_INFO, () => {
        clearTimeout(loadTimeout);
        setIsLoading(false);
        setIsPlaying(true);
        setError(null);
        console.log('FLV媒体信息加载完成');
      });

      // 添加更多事件监听来确保状态正确更新
      flvPlayer.on(flvjs.Events.SCRIPTDATA_ARRIVED, () => {
        clearTimeout(loadTimeout);
        setIsLoading(false);
        setIsPlaying(true);
        setError(null);
        console.log('FLV脚本数据到达');
      });

      flvPlayer.on(flvjs.Events.STATISTICS_INFO, (stats) => {
        // 如果有统计数据，说明播放器正在工作
        if (stats.currentSegmentIndex > 0 || stats.currentDecodedFrames > 0) {
          clearTimeout(loadTimeout);
          setIsLoading(false);
          setIsPlaying(true);
          setError(null);
          console.log('FLV播放统计:', stats);
        }
      });

      // 添加视频元素的事件监听
      videoRef.current.addEventListener('loadeddata', () => {
        clearTimeout(loadTimeout);
        setIsLoading(false);
        setIsPlaying(true);
        setError(null);
        
        // 播放成功，重置重试状态
        setRetryCount(0);
        setLastRetryTime(0);
        setIsRetrying(false);
        
        console.log(`${streamKey} 视频数据加载完成，重试状态已重置`);
        
        scheduleOrientDetect();
      });

      videoRef.current.addEventListener('canplay', () => {
        clearTimeout(loadTimeout);
        setIsLoading(false);
        setIsPlaying(true);
        setError(null);
        console.log('视频可以播放');
      });

      videoRef.current.addEventListener('playing', () => {
        clearTimeout(loadTimeout);
        setIsLoading(false);
        setIsPlaying(true);
        setError(null);
        
        // 播放成功，重置重试状态
        setRetryCount(0);
        setLastRetryTime(0);
        setIsRetrying(false);
        
        console.log(`${streamKey} 视频开始播放，重试状态已重置`);
      });

      flvPlayer.on(flvjs.Events.ERROR, (errorType, errorDetail) => {
        clearTimeout(loadTimeout);
        console.error('FLV播放错误:', errorType, errorDetail);
        
        // 只有在真正出现致命错误时才显示错误信息
        // 如果播放器已经成功播放，则忽略一些非致命错误
        if (!isPlaying) {
          setError('FLV播放失败');
          if (onError) onError('flv', { errorType, errorDetail });
          
          // 自动尝试智能重试
          setTimeout(() => {
            if (!isPlaying && !isRetrying) {
              smartRetry();
            }
          }, 1000);
        } else {
          console.warn('FLV播放过程中出现非致命错误，继续播放:', errorType, errorDetail);
        }
      });

      return true;
    } catch (error) {
      console.error('初始化FLV失败:', error);
      setError('FLV初始化失败');
      return false;
    }
  };

  // WebRTC 低延迟播放（SRS）
  const initWebRTC = async () => {
    if (!videoRef.current) {
      setError('视频元素未就绪');
      return false;
    }
    try {
      const streamUrl = streamUrls.pull?.webrtc || getWebRtcUrl(streamKey);
      const { stream, close } = await playSrsWebRtc(streamKey, { streamUrl });
      webrtcCloseRef.current = close;
      videoRef.current.srcObject = stream;
      videoRef.current.muted = finalMutedState;
      await videoRef.current.play().catch(() => {});
      setIsLoading(false);
      setIsPlaying(true);
      setError(null);
      setCurrentDelay(0.5);
      setRetryCount(0);
      setLastRetryTime(0);
      setIsRetrying(false);
      if (onSuccess) onSuccess('webrtc');
      console.log(`${streamKey} WebRTC播放成功`);
      scheduleOrientDetect();
      return true;
    } catch (error) {
      console.error('初始化WebRTC失败:', error);
      setError(`WebRTC失败: ${error.message || '未知错误'}`);
      if (onError) onError('webrtc', error);
      return false;
    }
  };

  // 手动开始播放
  const startPlayback = async () => {
    if (!streamUrls.pull || Object.keys(streamUrls.pull).length === 0) {
      setError('没有可用的流媒体URL');
      return;
    }
    
    // 检查资源管理器是否允许启动新播放器
    if (!ResourceManager.registerPlayer(streamKey)) {
      setError('已达到最大播放设备数量限制，请先停止其他设备');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setIsInitialized(true);
    
    cleanupMediaPlayers();
    
    // 清理延迟检测定时器
    if (delayCheckIntervalRef.current) {
      clearInterval(delayCheckIntervalRef.current);
      delayCheckIntervalRef.current = null;
    }
    if (accelerationIntervalRef.current) {
      clearInterval(accelerationIntervalRef.current);
      accelerationIntervalRef.current = null;
    }

    // 初始化播放器
    let success = false;
    switch (currentProtocol) {
      case 'webrtc':
        success = await initWebRTC();
        break;
      case 'hls':
        success = initHLS();
        break;
      case 'flv':
        success = initFLV();
        break;
      default:
        setError(`不支持的协议: ${currentProtocol}`);
        setIsLoading(false);
        return;
    }

    if (!success) {
      setIsLoading(false);
      setIsInitialized(false);
      ResourceManager.unregisterPlayer(streamKey);
    }
  };

  // 切换协议
  const switchProtocol = async (newProtocol) => {
    if (!isInitialized) {
      setCurrentProtocol(newProtocol);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setCurrentProtocol(newProtocol);

    cleanupMediaPlayers();

    // 初始化新协议播放器
    let success = false;
    switch (newProtocol) {
      case 'webrtc':
        success = await initWebRTC();
        break;
      case 'hls':
        success = initHLS();
        break;
      case 'flv':
        success = initFLV();
        break;
      default:
        setError(`不支持的协议: ${newProtocol}`);
        setIsLoading(false);
        return;
    }

    if (!success) {
      setIsLoading(false);
    }
  };

  // 播放/暂停
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // 静音/取消静音
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // 音量控制
  const handleVolumeChange = (value) => {
    if (videoRef.current) {
      videoRef.current.volume = value;
      setVolume(value);
      if (value === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    }
  };

  // 全屏切换（按钮 / 双击视频）
  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        document.exitFullscreen?.();
      } else if (!document.fullscreenElement) {
        el.requestFullscreen?.();
      } else {
        document.exitFullscreen?.().then(() => el.requestFullscreen?.()).catch(() => {});
      }
    } catch (err) {
      console.warn('全屏切换失败:', err);
    }
  }, []);

  // 智能重试逻辑
  const smartRetry = () => {
    const now = Date.now();
    const minRetryInterval = 3000; // 最小重试间隔3秒
    const maxRetryCount = 5; // 最大重试次数
    
    // 检查重试条件
    if (retryCount >= maxRetryCount) {
      console.log(`${streamKey} 已达到最大重试次数(${maxRetryCount})，停止重试`);
      setError(`播放失败，已重试${maxRetryCount}次`);
      return;
    }
    
    if (now - lastRetryTime < minRetryInterval) {
      console.log(`${streamKey} 重试间隔不足，跳过重试`);
      return;
    }
    
    if (isRetrying) {
      console.log(`${streamKey} 正在重试中，跳过重复重试`);
      return;
    }
    
    setIsRetrying(true);
    setRetryCount(prevCount => prevCount + 1);
    setLastRetryTime(now);
    
    console.log(`${streamKey} 开始第${retryCount + 1}次智能重试...`);
    
    // 计算退避延迟 (指数退避)
    const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 10000); // 最大10秒
    
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    
    retryTimeoutRef.current = setTimeout(() => {
      setIsLoading(true);
      setError(null);
      setIsRetrying(false);
      
      // 优先 WebRTC，失败再 FLV / HLS
      const protocolOrder = PROTOCOL_RETRY_ORDER;
      const currentIndex = protocolOrder.indexOf(currentProtocol);
      const nextProtocol = protocolOrder[(currentIndex + 1) % protocolOrder.length];
      
      console.log(`${streamKey} 第${retryCount}次重试，切换到协议: ${nextProtocol}`);
      switchProtocol(nextProtocol);
    }, backoffDelay);
  };

  // 重新加载 - 改为使用智能重试
  const reload = () => {
    // 重置重试计数器，允许手动重试
    setRetryCount(0);
    setLastRetryTime(0);
    setIsRetrying(false);
    
    setIsLoading(true);
    setError(null);
    switchProtocol(currentProtocol);
  };

  // 组件挂载时只获取流URL，不自动播放
  useEffect(() => {
    fetchStreamUrls();
    
    // 组件卸载时的清理函数
    return () => {
      console.log(`VideoPlayer组件卸载: ${streamKey}`);
      
      // 清理播放器
      cleanupMediaPlayers();
      
      // 清理视频元素
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
        videoRef.current.load();
      }
      
      // 清理定时器
      if (delayCheckIntervalRef.current) {
        clearInterval(delayCheckIntervalRef.current);
        delayCheckIntervalRef.current = null;
      }
      if (accelerationIntervalRef.current) {
        clearInterval(accelerationIntervalRef.current);
        accelerationIntervalRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      
      // 从资源管理器注销
      ResourceManager.unregisterPlayer(streamKey);
    };
  }, [streamKey]);

  // 仅在用户切换协议时重建播放器；不要因 streamUrls 对象引用变化反复重进
  useEffect(() => {
    if (isInitialized && streamUrls.pull && Object.keys(streamUrls.pull).length > 0) {
      switchProtocol(currentProtocol);
    }
  }, [currentProtocol]);

  // 全屏状态监听
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenNow = !!document.fullscreenElement;
      if (onFullscreenChange) {
        onFullscreenChange(isFullscreenNow);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [onFullscreenChange]);

  useEffect(() => () => {
    if (orientTimerRef.current) clearTimeout(orientTimerRef.current);
    if (orientPollRef.current) clearInterval(orientPollRef.current);
    if (rotateClickTimerRef.current) clearTimeout(rotateClickTimerRef.current);
  }, []);

  // 旋转按钮显示约 4 秒后自动收起（再次单击可重新打开）
  useEffect(() => {
    if (!showRotateHud) return undefined;
    const hideTimer = setTimeout(() => setShowRotateHud(false), 4000);
    return () => clearTimeout(hideTimer);
  }, [showRotateHud, rotationAngle, flipHorizontal, flipVertical]);

  // 全屏时每秒刷新时钟
  useEffect(() => {
    if (!isFullscreen) {
      setFullscreenClock('');
      return undefined;
    }
    const tick = () => {
      setFullscreenClock(formatFullscreenClock(new Date()));
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isFullscreen]);


  return (
    <div 
      ref={containerRef}
      className={`video-player-container ${isFullscreen ? 'fullscreen' : ''}`}
    >
      {/* 全屏常显：队员名 + 当前时间 + 设备电量 */}
      {isFullscreen && (
        <FullscreenInfoBar
          memberName={memberName}
          streamKey={streamKey}
          fullscreenClock={fullscreenClock}
          batteryLevel={batteryLevel}
          lastSeen={lastSeen}
        />
      )}

      <VideoHeader
        memberName={memberName}
        streamKey={streamKey}
        currentProtocol={currentProtocol}
        switchProtocol={switchProtocol}
      />

      <div
        className={`video-wrapper${videoOrientation === 'portrait' ? ' video-wrapper--portrait' : ''}${slotDragIndex != null ? ' video-wrapper--draggable' : ''}${showRotateHud ? ' video-wrapper--rotate-hud' : ''}`}
        style={getVideoContainerStyleLocal()}
        draggable={slotDragIndex != null}
        title="单击旋转 · 双击全屏 · 按住拖动换窗"
        onClick={handleSurfaceClick}
        onDragStart={(e) => {
          if (slotDragIndex == null) return;
          surfaceDraggedRef.current = true;
          e.dataTransfer.setData('text/plain', String(slotDragIndex));
          e.dataTransfer.effectAllowed = 'move';
          onSlotDragStart?.(slotDragIndex, e);
        }}
        onDragEnd={() => onSlotDragEnd?.()}
        onDoubleClick={handleSurfaceDoubleClick}
      >
        <video
          ref={videoRef}
          controls={false}
          autoPlay
          muted={finalMutedState}
          playsInline
          draggable={false}
          style={{ ...getVideoStyleLocal(), cursor: slotDragIndex != null ? 'grab' : 'pointer' }}
          onLoadedMetadata={scheduleOrientDetect}
          onLoadedData={scheduleOrientDetect}
          onDoubleClick={handleSurfaceDoubleClick}
        />

        {/* 手机端：单击画面时在顶部半透明显示状态信息（不占独立底栏） */}
        {hideOverlayControls && showRotateHud && isInitialized && !error && (
          <VideoStatusOverlay
            currentProtocol={currentProtocol}
            isPlaying={isPlaying}
            currentDelay={currentDelay}
            videoDimensions={videoDimensions}
            videoOrientation={videoOrientation}
            autoOrient={autoOrient}
            rotationAngle={rotationAngle}
            flipHorizontal={flipHorizontal}
            flipVertical={flipVertical}
            isAccelerating={isAccelerating}
            isInitialized={isInitialized}
          />
        )}

        {/* 单击画面出现：旋转 / 翻转（普通窗与全屏均可用） */}
        {showRotateHud && isInitialized && !error && (
          <RotateHud
            rotateLeft={rotateLeft}
            rotateRight={rotateRight}
            flipHorizontal={flipHorizontal}
            flipVertical={flipVertical}
            toggleFlipHorizontal={toggleFlipHorizontal}
            toggleFlipVertical={toggleFlipVertical}
            rotationAngle={rotationAngle}
            resetRotation={resetRotation}
            setShowRotateHud={setShowRotateHud}
          />
        )}
        
        {/* 加载指示器 - 只有在真正加载且没有播放成功时才显示 */}
        {isLoading && !isPlaying && (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <div>正在加载 {PROTOCOLS[currentProtocol]?.name} 流...</div>
          </div>
        )}
        
        {/* 手动播放按钮 - 只有在未初始化时才显示 */}
        {!isInitialized && !isLoading && (
          <div className="manual-play-overlay">
            <div className="manual-play-content">
              <div className="manual-play-text">点击开始播放</div>
              <Button 
                type="primary" 
                size="large" 
                icon={<PlayCircleOutlined />}
                onClick={startPlayback}
                style={{ marginTop: '10px' }}
              >
                开始播放
              </Button>
            </div>
          </div>
        )}

        {/* 错误提示 - 只有在真正有错误且没有播放成功时才显示 */}
        {error && !isPlaying && (
          <VideoErrorPanel
            error={error}
            retryCount={retryCount}
            isRetrying={isRetrying}
            reload={reload}
            onHardReset={() => {
              // 重置所有状态，强制重新开始
              setRetryCount(0);
              setLastRetryTime(0);
              setIsRetrying(false);
              setError(null);
              setIsInitialized(false);
              setIsPlaying(false);
            }}
          />
        )}

        {/*
          播放控制覆盖层：仅桌面鼠标悬停显示。
          手机端触摸会粘滞 :hover，控件条与画面来回切换闪烁，故移动端不渲染。
        */}
        {!hideOverlayControls && isInitialized && !isLoading && !error && (
          <VideoControlsBar
            isPlaying={isPlaying}
            togglePlay={togglePlay}
            autoOrient={autoOrient}
            toggleAutoOrient={toggleAutoOrient}
            rotateLeft={rotateLeft}
            rotateRight={rotateRight}
            flipHorizontal={flipHorizontal}
            flipVertical={flipVertical}
            toggleFlipHorizontal={toggleFlipHorizontal}
            toggleFlipVertical={toggleFlipVertical}
            resetRotation={resetRotation}
            finalMutedState={finalMutedState}
            toggleMute={toggleMute}
            volume={volume}
            handleVolumeChange={handleVolumeChange}
            toggleFullscreen={toggleFullscreen}
            onStopClick={() => {
              setIsPlaying(false);
              setIsInitialized(false);
              setError(null);
              setIsAccelerating(false);
              setCurrentDelay(0);
              cleanupMediaPlayers();
              if (delayCheckIntervalRef.current) {
                clearInterval(delayCheckIntervalRef.current);
                delayCheckIntervalRef.current = null;
              }
              if (accelerationIntervalRef.current) {
                clearInterval(accelerationIntervalRef.current);
                accelerationIntervalRef.current = null;
              }
              if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
              }
              ResourceManager.unregisterPlayer(streamKey);
              if (onStop) onStop();
            }}
          />
        )}
      </div>

      {/* PC 保留底栏；手机端改单击画面顶部半透明显示，见 video-status-overlay */}
      {!hideOverlayControls && (
        <VideoFooterStatus
          currentProtocol={currentProtocol}
          isPlaying={isPlaying}
          currentDelay={currentDelay}
          isAccelerating={isAccelerating}
          videoDimensions={videoDimensions}
          videoOrientation={videoOrientation}
          autoOrient={autoOrient}
          rotationAngle={rotationAngle}
          flipHorizontal={flipHorizontal}
          flipVertical={flipVertical}
          isInitialized={isInitialized}
        />
      )}
    </div>
  );
};

export default MultiWindowVideoPlayer;
